// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: is the person busy right now? The corner assistant never pops up during a call,
// voice mode, a screen share, while they are typing, or while the tab is hidden; it holds
// the suggestion until they are free (see Corner.ts).

import VoiceMode from '@app/features/konvo_assistant/voice/VoiceModeSession';
import {type CornerBusyReason, TYPING_IDLE_MS} from '@app/features/konvo_corner/state/CornerQueue';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import Window from '@app/features/window/state/Window';

let lastTypedAt = 0;
let listening = false;

function isTypingTarget(target: EventTarget | null): boolean {
	if (!(target instanceof HTMLElement)) return false;
	if (target.isContentEditable) return true;
	const tag = target.tagName;
	if (tag === 'TEXTAREA') return true;
	if (tag !== 'INPUT') return false;
	const type = (target as HTMLInputElement).type;
	return type === 'text' || type === 'search' || type === 'email' || type === '';
}

function onKeyDown(event: KeyboardEvent): void {
	if (event.metaKey || event.ctrlKey || event.altKey) return;
	if (event.key.length !== 1 && event.key !== 'Backspace' && event.key !== 'Enter') return;
	if (isTypingTarget(event.target)) lastTypedAt = Date.now();
}

/** Start noticing keystrokes in text fields (idempotent). */
export function watchTyping(): () => void {
	if (listening) return () => {};
	listening = true;
	document.addEventListener('keydown', onKeyDown, true);
	return () => {
		listening = false;
		document.removeEventListener('keydown', onKeyDown, true);
	};
}

export function isTyping(now = Date.now()): boolean {
	if (now - lastTypedAt > TYPING_IDLE_MS) return false;
	return isTypingTarget(document.activeElement);
}

/** Why the corner should wait right now, or null when the person is free. */
export function busyReason(now = Date.now()): CornerBusyReason | null {
	if (!Window.visible) return 'hidden_tab';
	if (LocalVoiceState.selfStream) return 'screen_share';
	if (VoiceMode.active) return 'in_call';
	if (MediaEngine.channelId && (MediaEngine.room || MediaEngine.connected)) return 'in_call';
	if (isTyping(now)) return 'typing';
	return null;
}
